// historyService.js - Service for handling patient history API calls

const BASE_URL = 'https://testingnew.webddocsystems.com/public/api/v1/webdoc/history';

/**
 * Save patient history
 * @param {Object} historyData - History data object
 * @param {string} historyData.DoctorId - Doctor ID
 * @param {string} historyData.PatientId - Patient ID
 * @param {string} historyData.PastSurgicalhistory - Past surgical history
 * @param {string} historyData.PastMedicalhistory - Past medical history
 * @param {string} historyData.Drughistory - Drug history
 * @param {string} historyData.GyneorOBShistory - Gynecological or OBS history
 * @returns {Promise<Object>} API response
 */
export const savePatientHistory = async (historyData) => {
  try {
    const formData = new FormData();
    formData.append('DoctorId', historyData.DoctorId);
    formData.append('PatientId', historyData.PatientId);
    formData.append('PastSurgicalhistory', historyData.PastSurgicalhistory);
    formData.append('PastMedicalhistory', historyData.PastMedicalhistory);
    formData.append('Drughistory', historyData.Drughistory);
    formData.append('GyneorOBShistory', historyData.GyneorOBShistory);

    const response = await fetch(`${BASE_URL}/save`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    // Check API response statusCode for success
    if (result.statusCode === 1) {
      return {
        success: true,
        data: result.payLoad,
        message: result.statusMessage ? result.statusMessage.join(', ') : 'History saved successfully'
      };
    } else {
      return {
        success: false,
        error: result.statusMessage ? result.statusMessage.join(', ') : 'Unknown error',
        message: 'Failed to save history'
      };
    }
  } catch (error) {
    console.error('Error saving patient history:', error);
    return {
      success: false,
      error: error.message,
      message: 'Failed to save history'
    };
  }
};

/**
 * Update patient history
 * @param {Object} historyData - History data object
 * @param {string} historyData.Id - History record ID
 * @param {string} historyData.DoctorId - Doctor ID
 * @param {string} historyData.PastSurgicalhistory - Past surgical history
 * @param {string} historyData.PastMedicalhistory - Past medical history
 * @param {string} historyData.Drughistory - Drug history
 * @param {string} historyData.GyneorOBShistory - Gynecological or OBS history
 * @returns {Promise<Object>} API response
 */
export const updatePatientHistory = async (historyData) => {
  try {
    const formData = new FormData();
    formData.append('Id', historyData.Id);
    formData.append('DoctorId', historyData.DoctorId);
    formData.append('PastSurgicalhistory', historyData.PastSurgicalhistory);
    formData.append('PastMedicalhistory', historyData.PastMedicalhistory);
    formData.append('Drughistory', historyData.Drughistory);
    formData.append('GyneorOBShistory', historyData.GyneorOBShistory);

    const response = await fetch(`${BASE_URL}/update`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    // Check API response statusCode for success
    if (result.statusCode === 1) {
      return {
        success: true,
        data: result.payLoad,
        message: result.statusMessage ? result.statusMessage.join(', ') : 'History updated successfully'
      };
    } else {
      return {
        success: false,
        error: result.statusMessage ? result.statusMessage.join(', ') : 'Unknown error',
        message: 'Failed to update history'
      };
    }
  } catch (error) {
    console.error('Error updating patient history:', error);
    return {
      success: false,
      error: error.message,
      message: 'Failed to update history'
    };
  }
};

/**
 * View patient history
 * @param {string} patientId - Patient ID
 * @returns {Promise<Object>} API response
 */
export const viewPatientHistory = async (patientId) => {
  try {
    const response = await fetch(`${BASE_URL}/view?PatientId=${patientId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    // Check API response statusCode
    if (result.statusCode === 1 && result.payLoad) {
      // Data exists - return success with payload
      return {
        success: true,
        data: result.payLoad,
        hasExistingData: true,
        message: 'History retrieved successfully'
      };
    } else if (result.statusCode === 3 || !result.payLoad) {
      // No data found - return success but indicate no existing data
      return {
        success: true,
        data: null,
        hasExistingData: false,
        message: 'No existing history found'
      };
    } else {
      // Other status codes - treat as error
      return {
        success: false,
        data: null,
        hasExistingData: false,
        error: result.statusMessage ? result.statusMessage.join(', ') : 'Unknown error',
        message: 'Failed to fetch history'
      };
    }
  } catch (error) {
    console.error('Error fetching patient history:', error);
    return {
      success: false,
      data: null,
      hasExistingData: false,
      error: error.message,
      message: 'Failed to fetch history'
    };
  }
};